import { Injectable } from '@angular/core'
import { map, Observable } from 'rxjs'

import {
  EstimateCategoryModel,
  EstimateCategoryStatus,
  EstimateTemplateData,
  EstimateTemplateModel,
} from '../model/estimate'
import { EstimateTemplateApiService } from './estimate-template-api.service'
import { PagedRestResponse, RestResponse } from './rest'
import { ParamsObject } from './rest/rest.service'

export interface EstimateTemplateCatalogTemplatesQuery extends ParamsObject {
  categoryId?: string
  all?: boolean
  pageIndex?: number
  pageSize?: number
  name?: string
}

export interface EstimateTemplateCatalogPagedResponse<T> {
  data: T[]
  count: number
  total: number
  startAt?: string
}

export interface EstimateTemplateCatalogCategoriesQuery extends ParamsObject {
  parentId?: string
  name?: string
  pageIndex?: number
  pageSize?: number
  startAt?: string
}

@Injectable({
  providedIn: 'root',
})
export class EstimateTemplateCatalogService {
  constructor(private estimateTemplateApiService: EstimateTemplateApiService) {}

  getCategories(query: EstimateTemplateCatalogCategoriesQuery = {}, forceRefresh = false): Observable<EstimateCategoryModel[]> {
    return this.estimateTemplateApiService.getEstimateCategories(query, forceRefresh).pipe(
      map((categories) => {
        if ('pageIndex' in query) {
          return categories
        }

        return this.orderHierarchy(categories)
      }),
    )
  }

  getTemplates(query: EstimateTemplateCatalogTemplatesQuery, forceRefresh = false): Observable<EstimateTemplateModel[]> {
    return this.estimateTemplateApiService.getEstimateTemplatesCollection(query, forceRefresh)
  }

  getTemplatesPage(query: EstimateTemplateCatalogTemplatesQuery, forceRefresh = false): Observable<EstimateTemplateCatalogPagedResponse<EstimateTemplateModel>> {
    return this.estimateTemplateApiService.getEstimateTemplatesCollectionPage(query, forceRefresh).pipe(
      map((response: PagedRestResponse<EstimateTemplateModel[]>) => {
        return {
          data: response.data,
          count: response.count ?? response.data.length,
          total: response.total ?? response.count ?? response.data.length,
          startAt: response.startAt,
        }
      }),
    )
  }

  getCategoriesPage(query: EstimateTemplateCatalogCategoriesQuery = {}, forceRefresh = false): Observable<EstimateTemplateCatalogPagedResponse<EstimateCategoryModel>> {
    return this.estimateTemplateApiService.getEstimateCategoriesPage(query, forceRefresh).pipe(
      map((response: PagedRestResponse<EstimateCategoryModel[]>) => {
        return {
          data: response.data,
          count: response.count ?? response.data.length,
          total: response.total ?? response.count ?? response.data.length,
          startAt: response.startAt,
        }
      }),
    )
  }

  createOrUpdateCategory(input: {
    id?: string
    name: string
    parentId?: string | null
    status: EstimateCategoryStatus
  }): Observable<RestResponse<EstimateCategoryModel>> {
    if (input.id) {
      return this.estimateTemplateApiService.updateEstimateCategory(input.id, {
        name: input.name,
        parentId: input.parentId ?? null,
        status: input.status,
      })
    }

    return this.estimateTemplateApiService.createEstimateCategory({
      name: input.name,
      parentId: input.parentId ?? null,
      status: input.status,
    })
  }

  deleteCategory(id: string) {
    return this.estimateTemplateApiService.deleteEstimateCategory(id)
  }

  createOrUpdateTemplate(input: {
    id?: string
    name: string
    facilityIds: string[]
    categoryId?: string | null
    charges: EstimateTemplateData['charges']
  }): Observable<RestResponse<EstimateTemplateModel>> {
    if (input.id) {
      return this.estimateTemplateApiService.updateEstimateTemplate(input.id, {
        name: input.name,
        facilityIds: input.facilityIds,
        categoryId: input.categoryId ?? null,
        charges: input.charges,
      })
    }

    return this.estimateTemplateApiService.createEstimateTemplate({
      name: input.name,
      facilityIds: input.facilityIds,
      categoryId: input.categoryId ?? null,
      charges: input.charges,
    })
  }

  deleteTemplate(id: string) {
    return this.estimateTemplateApiService.deleteEstimateTemplate(id)
  }

  private orderHierarchy(categories: EstimateCategoryModel[]): EstimateCategoryModel[] {
    const categoriesMap = new Map(categories.map(category => [category.id, category]))
    const childrenByParentId = new Map<string | null, EstimateCategoryModel[]>()

    for (const category of categories) {
      const parentId = category.parentId && categoriesMap.has(category.parentId)
        ? category.parentId
        : null

      if (!childrenByParentId.has(parentId)) {
        childrenByParentId.set(parentId, [])
      }

      childrenByParentId.get(parentId)?.push(category)
    }

    const sorted = Array.from(childrenByParentId.entries()).reduce((tree, [parentId, children]) => {
      tree.set(parentId, [...children].sort((a, b) => a.name.localeCompare(b.name)))
      return tree
    }, new Map<string | null, EstimateCategoryModel[]>())

    const result: EstimateCategoryModel[] = []
    const visited = new Set<string>()

    const visit = (category: EstimateCategoryModel, depth: number) => {
      if (visited.has(category.id)) {
        return
      }

      visited.add(category.id)
      category.depth = depth
      result.push(category)

      const children = sorted.get(category.id) || []
      for (const child of children) {
        visit(child, depth + 1)
      }
    }

    for (const root of sorted.get(null) || []) {
      visit(root, 0)
    }

    for (const category of categories) {
      if (!visited.has(category.id)) {
        visit(category, 0)
      }
    }

    return result
  }
}
